import { Link } from '@tanstack/react-router';
import type { ActivitySummary } from '@painpal/contracts';
import type { SportFilter } from '../../api/keys';
import { useRetryActivity } from '../../api/mutations';
import type { useActivities } from '../../api/queries';
import { LoadCell } from '../../components/LoadCell';
import { SourceGlyph } from '../../components/SourceGlyph';
import { LoadMethodBadge } from '../../components/LoadMethodBadge';
import { SportTag } from '../../components/SportTag';
import { ViewOnStrava } from '../../components/ViewOnStrava';
import { EmptyState, ErrorPanel, LoadingBlock } from '../../components/StateViews';
import { errorMessage } from '../../lib/api';
import { formatDuration, formatKm, formatMonthDay } from '../../lib/format';
import { sportCount } from '../../lib/sports';
import { keyMetric, metaLine, pageTotal, pendingLabel, sportSearch, subType } from './activitiesModel';
import styles from './activities.module.css';

const HEADERS = ['DATE', 'SPORT', 'NAME', 'DURATION', 'DISTANCE', 'KEY METRIC', 'LOAD', 'HOW', 'SOURCE'];

interface RowProps { activity: ActivitySummary; onRetry: (id: string) => void; retrying: boolean; recalcFrom: string | null }

function NameCell({ activity }: { activity: ActivitySummary }) {
  const type = subType(activity);
  return (
    <td data-cell="name">
      <span className={styles.nameRow}>
        <Link to="/activities/$activityId" params={{ activityId: activity.id }} className={styles.name}>{activity.name.toUpperCase()}</Link>
        {type !== null && <span className={styles.subType}>{`· ${type}`}</span>}
        <SourceGlyph source={activity.source} />
      </span>
      <span data-part="meta" className={styles.meta}>{metaLine(activity)}</span>
      <span data-part="badge" className={styles.phoneBadge}><LoadMethodBadge activity={activity} /></span>
      {activity.stravaUrl !== null && <ViewOnStrava href={activity.stravaUrl} className={styles.stravaLink} />}
      {activity.status === 'failed' && activity.error !== null && <span data-part="error" className={styles.rowError}>{activity.error}</span>}
    </td>
  );
}

function Row({ activity, onRetry, retrying, recalcFrom }: RowProps) {
  return (
    <tr data-status={activity.status}>
      <td data-cell="date">{formatMonthDay(activity.localDate)}</td>
      <td data-cell="sport"><SportTag sport={activity.sport} /></td>
      <NameCell activity={activity} />
      <td data-cell="duration">{formatDuration(activity.durationS)}</td>
      <td data-cell="distance">{formatKm(activity.distanceM)}</td>
      <td data-cell="metric">{keyMetric(activity)}</td>
      <td data-cell="load">
        <span className={styles.loadCell}>
          <LoadCell activity={activity} pxPerLoad={0.22} maxBarPx={90} showReason={false} onRetry={onRetry} retrying={retrying} pendingLabel={pendingLabel(activity, recalcFrom)} />
        </span>
      </td>
      <td data-cell="how"><LoadMethodBadge activity={activity} /></td>
      <td data-cell="source" className={styles.source}>{activity.source === 'fit' ? 'FIT FILE' : 'STRAVA'}</td>
    </tr>
  );
}

function Footer({ query, shown, total }: { query: ReturnType<typeof useActivities>; shown: number; total: number }) {
  return (
    <div className={styles.footer}>
      <div className={styles.legend}>
        <span className={styles.legendItem}><SourceGlyph source="fit" />FIT FILE</span>
        <span className={styles.legendItem}><SourceGlyph source="strava" />STRAVA</span>
      </div>
      <span className={styles.key}>BIG NUMBER = LOAD · BADGE = HOW IT WAS WORKED OUT</span>
      <span className={styles.count}>SHOWING {shown} OF {total}<span className="sr-only-phone"> · KEY METRIC = W. POWER FOR RIDES, AVG PACE FOR RUNS, AVG HR FOR THE REST</span></span>
      {query.hasNextPage && (
        <button type="button" className={`btn btn-outline ${styles.more}`} disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
          {query.isFetchingNextPage ? 'LOADING…' : 'LOAD MORE ↓'}
        </button>
      )}
    </div>
  );
}

interface ActivityTableProps { query: ReturnType<typeof useActivities>; items: ActivitySummary[]; sport: SportFilter; recalcFrom: string | null }

function Body({ query, items, sport, recalcFrom }: ActivityTableProps) {
  const retry = useRetryActivity();
  if (query.isPending) return <LoadingBlock label="LOADING ACTIVITIES" />;
  if (query.isError) return <ErrorPanel error={query.error} onRetry={() => void query.refetch()} />;
  if (items.length === 0) {
    return (
      <EmptyState title={`NO ${sportCount(sport, 2).slice(2)} HERE YET`} body="Upload .FIT files or sync Strava and they appear here, newest first.">
        <Link to="/activities" search={{ ...sportSearch(sport), upload: true }} className="btn btn-ink">+ UPLOAD .FIT FILES</Link>
      </EmptyState>
    );
  }
  return (
    <>
      {retry.isError && <p role="alert" className={styles.retryError}>{errorMessage(retry.error)}</p>}
      <table className={`data-table ${styles.table}`}>
        <thead className="sr-only-phone"><tr>{HEADERS.map((h) => <th key={h} scope="col">{h}</th>)}</tr></thead>
        <tbody>
          {items.map((a) => (
            <Row key={a.id} activity={a} recalcFrom={recalcFrom} onRetry={(id) => retry.mutate(id)} retrying={retry.isPending && retry.variables === a.id} />
          ))}
        </tbody>
      </table>
      <Footer query={query} shown={items.length} total={pageTotal(query.data.pages)} />
    </>
  );
}

export function ActivityTable(props: ActivityTableProps) {
  return (
    <section className={styles.tablePanel} aria-labelledby="all-title">
      <header className="dark-head">
        <h2 id="all-title" className="dark-title">ALL ACTIVITIES</h2>
        <span className={styles.darkNote}>NEWEST FIRST ↓</span>
      </header>
      <Body {...props} />
    </section>
  );
}
